import { useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { BekebiWordmark } from '../components/brand';
import {
  BankIcon,
  CalendarIcon,
  CloseIcon,
  HistoryIcon,
  HomeIcon,
  LayersIcon,
  ListIcon,
  MailIcon,
  MenuIcon,
  PulseIcon,
  ServicesIcon,
  UsersIcon,
} from '../components/icons';
import { useCurrentUser } from '../session/current-user';
import { roleLabel } from '../session/roles';
import { AccountMenu } from './account-menu';
import { navForRole, visibleNav } from './navigation';

const NAV_ID = 'portal-navigation';

const ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  dashboard: HomeIcon,
  services: ServicesIcon,
  monitoring: PulseIcon,
  renewals: CalendarIcon,
  infrastructure: LayersIcon,
  'payment-account': BankIcon,
  users: UsersIcon,
  activity: ListIcon,
  mailboxes: MailIcon,
  'my-activity': HistoryIcon,
};

/**
 * The portal shell for both views: a compact top bar (brand, the view's name, account menu), the
 * role's navigation filtered by the session's permissions, and the main content. Admin gets the
 * Admin navigation; ICT (internal role `operations`) the ICT navigation.
 *
 * Layout: on large screens the navigation is a persistent sidebar; on smaller screens it opens from
 * the Menu button as a panel below the top bar (non-modal: nothing is trapped). The panel closes on
 * Escape (focus returns to the button), on navigation, and on the button.
 */
export function PortalShell() {
  const user = useCurrentUser();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const sections = visibleNav(user.permissions, navForRole(user.role));
  const view = roleLabel(user.role);

  // Close the mobile panel after navigating.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // While the mobile panel is open: focus its first link; Escape closes it.
  useEffect(() => {
    if (!navOpen) return;
    navRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  return (
    <div className="min-h-screen bg-canvas">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:text-fg"
      >
        Skip to main content
      </a>

      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line bg-sidebar/95 px-4 backdrop-blur sm:px-6">
        <button
          ref={menuButtonRef}
          type="button"
          aria-expanded={navOpen}
          aria-controls={NAV_ID}
          onClick={() => setNavOpen((value) => !value)}
          className="-ml-1 flex min-h-10 shrink-0 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-fg-2 hover:bg-raised hover:text-fg lg:hidden"
        >
          {navOpen ? <CloseIcon /> : <MenuIcon />}
          <span className="sr-only sm:not-sr-only">{navOpen ? 'Close menu' : 'Menu'}</span>
        </button>
        <BekebiWordmark view={view} />
        <div className="ml-auto min-w-0">
          <AccountMenu />
        </div>
      </header>

      <div className="lg:flex lg:min-h-[calc(100vh-4rem)]">
        <nav
          id={NAV_ID}
          ref={navRef}
          aria-label={view}
          className={`${navOpen ? 'block' : 'hidden'} border-b border-line bg-sidebar lg:block lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r`}
        >
          <div className="px-3 py-5 lg:sticky lg:top-16 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">
            {sections.map((section) => (
              <div key={section.label} className="mb-6 last:mb-0">
                <p
                  id={`nav-${section.label}`}
                  className="px-3 pb-2 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-faint"
                >
                  {section.label}
                </p>
                <ul aria-labelledby={`nav-${section.label}`} className="space-y-0.5">
                  {section.items.map((item) => {
                    const ItemIcon = ICONS[item.path] ?? ListIcon;
                    return (
                      <li key={item.path}>
                        <NavLink
                          to={`/app/${item.path}`}
                          className={({ isActive }) =>
                            `group relative flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm transition-colors ${
                              isActive
                                ? 'bg-raised font-semibold text-fg before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-linear-to-b before:from-brand-primary before:to-brand-primary-light'
                                : 'text-fg-2 hover:bg-raised/60 hover:text-fg'
                            }`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <ItemIcon
                                className={`size-[1.15rem] shrink-0 ${isActive ? 'text-brand-primary-light' : 'text-muted group-hover:text-fg-2'}`}
                              />
                              {item.label}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <main
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 px-4 pb-16 pt-8 focus:outline-none sm:px-6 lg:px-10 lg:pt-10"
        >
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Structural placeholder for a section without a screen (used by shell tests). It names the
 * destination and nothing else — no data, no invented figures.
 */
export function SectionPlaceholder({ title }: { title: string }) {
  return (
    <div className="max-w-3xl">
      <h1>{title}</h1>
      <p className="mt-2 text-sm text-muted">This section is added in a later stage.</p>
    </div>
  );
}

export function ShellNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Page not found</h1>
      <p className="mt-2 text-sm text-muted">There is nothing at this address.</p>
    </div>
  );
}
